import React, { useRef } from "react";
import { Link as Element, scroller } from "react-scroll";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import "../css/documentation.css";

const Documentation = () => {
    const contentRef = useRef(null);

    const sections = [
        {
            id: "introduction",
            title: "Introduction",
            content: (
                <>
                    <p>
                        Welcome to the documentation! This guide is designed to provide you with a comprehensive overview of our product's features, functionalities, and best practices. Whether you're new to our platform or have prior experience, you'll find valuable insights to help you make the most of your journey.
                    </p>
                    <h3>Purpose of This Guide</h3>
                    <p>
                        The primary aim of this documentation is to empower you with the knowledge and tools needed to effectively utilize our product. We've structured the content to cater to users of all experience levels, ensuring that beginners can easily follow along while experienced users can quickly access advanced features.
                    </p>
                    <h3>What to Expect</h3>
                    <ul>
                        <li><strong>Clarity:</strong> Simple, jargon-free language that makes understanding easy.</li>
                        <li><strong>Practical Examples:</strong> Step-by-step instructions with relevant use cases.</li>
                        <li><strong>Supportive Resources:</strong> Links to tutorials, videos, and additional documentation to enhance your learning.</li>
                    </ul>
                </>
            ),
        },
        {
            id: "getting-started",
            title: "Getting Started",
            content: (
                <>
                    <p>
                        Starting with our product is a straightforward process that ensures you�re set up for success right from the beginning. Follow these steps to get started and make the most of our platform.
                    </p>
                    <h3>Step 1: System Requirements</h3>
                    <ul>
                        <li><strong>Browser Compatibility:</strong> Use the latest version of Chrome, Firefox, Safari, or Edge for optimal performance.</li>
                        <li><strong>Internet Connection:</strong> A stable and fast connection is essential for a seamless experience.</li>
                    </ul>
                    <h3>Step 2: Registration</h3>
                    <p>
                        Visit our website and click on "Sign Up." Provide a valid email address and create a secure password. Verify your email address to complete the registration process.
                    </p>
                    <h3>Step 3: Initial Setup</h3>
                    <ul>
                        <li><strong>Customizing Your Dashboard:</strong> Choose layouts and widgets tailored to your workflow.</li>
                        <li><strong>Linking Integrations:</strong> Connect tools such as Slack, Google Drive, or Microsoft Teams to streamline your workflow.</li>
                        <li><strong>Setting Preferences:</strong> Adjust notifications, themes, and other settings to suit your needs.</li>
                    </ul>
                </>
            ),
        },
        {
            id: "features",
            title: "Features",
            content: (
                <>
                    <p>
                        Our product is packed with innovative features designed to enhance productivity, foster collaboration, and simplify complex workflows. Here�s an overview of what you can expect.
                    </p>
                    <h3>Key Features</h3>
                    <ul>
                        <li>
                            <strong>Real-Time Collaboration:</strong>
                            <ul>
                                <li>Collaborate with your team in real time, enabling instant feedback and updates.</li>
                                <li>Share files, assign tasks, and track progress seamlessly, ensuring everyone stays aligned.</li>
                            </ul>
                        </li>
                        <li>
                            <strong>Customizable Dashboards:</strong>
                            <ul>
                                <li>Design your workspace with intuitive drag-and-drop tools.</li>
                                <li>Add widgets to display metrics, progress, and notifications at a glance.</li>
                            </ul>
                        </li>
                        <li>
                            <strong>Powerful Analytics:</strong>
                            <ul>
                                <li>Utilize advanced reporting tools to gain insights into your projects.</li>
                                <li>Create detailed visualizations to track performance and identify areas for improvement.</li>
                            </ul>
                        </li>
                        <li>
                            <strong>Automated Processes:</strong>
                            <ul>
                                <li>Save time by automating repetitive tasks such as data entry, reminders, and notifications.</li>
                                <li>Focus on high-priority tasks while the system handles the routine.</li>
                            </ul>
                        </li>
                    </ul>
                    <h3>Designed for Scalability</h3>
                    <p>
                        Each feature is built with growth in mind, ensuring that our platform adapts to your evolving needs without compromising on usability.
                    </p>
                </>
            ),
        },
        {
            id: "faq",
            title: "FAQ",
            content: (
                <>
                    <h3>General Questions</h3>
                    <p><strong>Q: How do I reset my password?</strong></p>
                    <p>A: Click on the "Forgot Password" link on the login page, and follow the instructions sent to your registered email address to set a new password.</p>
                    <p><strong>Q: Is there a mobile version of the platform?</strong></p>
                    <p>A: Yes, our platform is fully responsive and works flawlessly on mobile browsers. Additionally, you can download our mobile app from the App Store or Google Play for an optimized experience.</p>
                    <p><strong>Q: Can I integrate this with other tools?</strong></p>
                    <p>A: Absolutely! We support integrations with popular platforms such as Slack, Google Workspace, and Microsoft Teams. Check the integrations section in the dashboard for a complete list and setup instructions.</p>
                    <h3>Technical Support</h3>
                    <p>
                        If your question isn�t covered here, please consult our Help Center or contact our support team for personalized assistance.
                    </p>
                </>
            ),
        },
        {
            id: "contact",
            title: "Contact Us",
            content: (
                <>
                    <p>
                        We are here to help you every step of the way. Whether you need technical support, have feedback, or just want to learn more about our product, our team is ready to assist you.
                    </p>
                    <h3>Support Channels</h3>
                    <ul>
                        <li><strong>Email Support:</strong> Reach us at support@example.com. Our team aims to respond within 24 hours.</li>
                        <li><strong>Phone Support:</strong> Call us at +1-800-555-1234. Available from 9 AM to 5 PM, Monday to Friday.</li>
                        <li><strong>Live Chat:</strong> Use the chat widget on our website for real-time assistance.</li>
                    </ul>
                    <h3>Feedback and Suggestions</h3>
                    <p>
                        We value your input! If you have suggestions for improving our platform or encounter issues, please share them with us. Your feedback helps us refine our product to better meet your needs.
                    </p>
                    <h3>Additional Resources</h3>
                    <p>
                        Visit our Help Center and Blog for tutorials, case studies, and tips to maximize your experience with our platform.
                    </p>
                </>
            ),
        },
    ];

    const handleMenuClick = (id) => {
        scroller.scrollTo(id, {
            duration: 500,
            smooth: true,
            containerId: "content",
            offset: -10,
        });
    };

    return (
        <div className="documentation-container">
            <nav className="menu">
                <Link className="textlink" to="/">
                    <p>
                        <FontAwesomeIcon icon={faArrowLeft} /> &nbsp; Return to home page
                    </p>
                </Link>
                <h3 className="textlink DocumentationTitle">DOCUMENTATION</h3>
                <ul>
                    {sections.map((section) => (
                        <li key={section.id}>
                            <button
                                onClick={() => handleMenuClick(section.id)}
                                className="menu-button text"
                            >
                                {section.title}
                            </button>
                        </li>
                    ))}
                </ul>
            </nav>
            <div id="content" className="content" ref={contentRef}>
                {sections.map((section) => (
                    <Element key={section.id} name={section.id} className="section" to="">
                        <h2 className="textlink">{section.title}</h2>
                        <div className="text">{section.content}</div>
                        <br></br><br></br>
                    </Element>
                ))}
            </div>
        </div>
    );
};

export default Documentation;
